import {
  Node,
  ReactNodeViewRenderer,
  NodeViewWrapper,
  type ReactNodeViewProps,
} from '@tiptap/react';
import { NulaLink } from './nula-link';

export interface InsertNulaLinkOptions {
  documentId: string;
  title: string;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    nulaLink: {
      /** Insert an internal document-link chip at the current selection. */
      insertNulaLink: (options: InsertNulaLinkOptions) => ReturnType;
    };
  }
}

/** React node view — renders the existing NulaLink chip inside the editor. */
function NulaLinkNodeView({ node }: ReactNodeViewProps) {
  const documentId = (node.attrs.documentId as string) ?? '';
  const title = (node.attrs.title as string) ?? '';
  // Inline, non-editable atom — wrap as a span so it sits in the text flow.
  return (
    <NodeViewWrapper as="span" data-nula-link-view>
      <NulaLink documentId={documentId} title={title} />
    </NodeViewWrapper>
  );
}

/**
 * Internal document link ("nula-link") — an inline atom node carrying a
 * documentId + title. Renders as the {@link NulaLink} chip (file icon + live
 * title, click-to-navigate). Serializes to a `<span data-nula-link>` so it
 * round-trips through the Yjs doc / HTML clipboard.
 *
 * Insert via the `insertNulaLink` command (wired to the "Link to document"
 * slash command + doc picker).
 */
export const NulaLinkExtension = Node.create({
  name: 'nulaLink',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      documentId: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-nula-link') ?? '',
        renderHTML: (attrs) => ({ 'data-nula-link': attrs.documentId as string }),
      },
      title: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-title') ?? el.textContent ?? '',
        renderHTML: (attrs) => ({ 'data-title': attrs.title as string }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-nula-link]' }];
  },

  renderHTML({ HTMLAttributes }) {
    // HTMLAttributes already carries data-nula-link + data-title from the
    // attribute renderHTML fns above; add the type marker for parse symmetry.
    return ['span', { ...HTMLAttributes, 'data-link-type': 'document' }];
  },

  renderMarkdown(node) {
    const title = String(node.attrs?.title ?? '').replace(/[!-/:-@[-`{-~]/g, '\\$&');
    return `[${title}](nula://document/${String(node.attrs?.documentId ?? '')})`;
  },

  addNodeView() {
    return ReactNodeViewRenderer(NulaLinkNodeView);
  },

  addCommands() {
    return {
      insertNulaLink:
        ({ documentId, title }: InsertNulaLinkOptions) =>
        ({ commands }: { commands: import('@tiptap/core').SingleCommands }) => {
          if (!documentId) return false;
          return commands.insertContent({
            type: this.name,
            attrs: { documentId, title },
          });
        },
    };
  },
});
